import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useQueries } from '@tanstack/react-query'
import { postNamedQuery } from '../../../service'
import { useStatusReportSupplementalData } from '../../../api/groups'
import { StatusReportItemSupplementalContentContext } from './StatusReportItemSupplementalContentContext'
import { useStatusReportContext } from './statusReportContext'
import { indexStatusReportItemsById } from './supplementalLayouts/pipeline/indexStatusReportItemsById'
import { expandSupplementalPipelineSources } from './supplementalLayouts/pipeline/expandSupplementalPipelineSources'
import { buildSupplementalPipelineExecuteVariables, buildSmartEndpointVariables } from './supplementalLayouts/pipeline/resolveSupplementalPipelineVariables'
import { filterPipelineRowsForLine, wrapMatchedRowsForDisplay } from './supplementalLayouts/pipeline/supplementalRowMatch'
import { getNested } from './supplementalLayouts/utils'

const SUPPORTED_SOURCE_TYPES = new Set(['pipeline', 'smartEndpoint'])

function fetchPipelineSource (sourceKey, templateVars, context) {
  const variables = buildSupplementalPipelineExecuteVariables(templateVars, context)
  return postNamedQuery('coreData', 'execute-data-pipeline', { pipelineCode: sourceKey, variables })
}

function getSmartEndpointResponseItemsPath (displayConfig) {
  return displayConfig?.responseItemsPath ?? 'tasks'
}

function fetchSmartEndpointSource (sourceKey, templateVars, context) {
  const variables = buildSmartEndpointVariables(templateVars, context)
  return postNamedQuery('coreData', 'invoke-smart-endpoint', { pipelineCode: sourceKey, variables })
}

function buildFetchGroupKey (source) {
  return JSON.stringify([
    source?.sourceType ?? null,
    source?.sourceKey ?? null,
    source?.pipelineVariables ?? null,
    source?.sourceType === 'smartEndpoint'
      ? getSmartEndpointResponseItemsPath(source?.displayConfig)
      : null
  ])
}

function getResponseRows (result, sourceType, responseItemsPath) {
  if (sourceType === 'smartEndpoint') {
    return getNested(result, responseItemsPath)
  }
  return result?.data
}

function StatusReportItemSupplementalContentProvider ({ children }) {
  const { report, config } = useStatusReportContext()
  const statusReportId = report?.statusReportId
  const clientId = report?.levelId
  const supplementalDataCode = config?.supplementalDataCode
  const shouldLoadSupplemental = !!(supplementalDataCode || report?.hasSupplementalData)

  const { data: configData, isLoading: configLoading } = useStatusReportSupplementalData(statusReportId, {
    supplementalDataCode,
    enabled: shouldLoadSupplemental
  })

  const itemByStatusReportItemId = useMemo(
    () => indexStatusReportItemsById(report?.items ?? []),
    [report?.items]
  )

  const expandedSources = useMemo(() => {
    const raw = configData?.supplementalSources ?? []
    return expandSupplementalPipelineSources(raw, itemByStatusReportItemId)
      .filter(s => SUPPORTED_SOURCE_TYPES.has(s.sourceType) && s.sourceKey)
  }, [configData?.supplementalSources, itemByStatusReportItemId])

  const byFetchGroup = useMemo(() => {
    const map = {}
    for (const s of expandedSources) {
      const key = buildFetchGroupKey(s)
      if (!map[key]) {
        map[key] = {
          sourceType: s.sourceType,
          sourceKey: s.sourceKey,
          pipelineVariables: s.pipelineVariables ?? null,
          responseItemsPath: s.sourceType === 'smartEndpoint'
            ? getSmartEndpointResponseItemsPath(s.displayConfig)
            : null,
          items: []
        }
      }
      map[key].items.push(s)
    }
    return map
  }, [expandedSources])

  const dataQueries = useQueries({
    queries: Object.values(byFetchGroup).map(({ sourceType, sourceKey, pipelineVariables, responseItemsPath, items }) => ({
      queryKey: [
        'status-report-supplemental',
        sourceType,
        statusReportId,
        supplementalDataCode ?? '_all',
        sourceKey,
        pipelineVariables,
        responseItemsPath
      ],
      queryFn: async () => {
        const context = { clientId, statusReportId }
        const fetchFn = sourceType === 'smartEndpoint' ? fetchSmartEndpointSource : fetchPipelineSource
        const { data } = await fetchFn(sourceKey, pipelineVariables, context)
        return { sourceType, sourceKey, responseItemsPath, items, result: data }
      },
      enabled: !!statusReportId && !!clientId && shouldLoadSupplemental && !!expandedSources.length
    }))
  })

  const dataSignal = dataQueries.map(q => q.dataUpdatedAt).join(',')
  const supplementalData = useMemo(() => {
    const merged = {}
    const pushSource = (itemId, itemData, matchedItem, sourceKey) => {
      const key = String(itemId)
      if (!merged[key]) merged[key] = { sources: [] }
      merged[key].sources.push({
        supplementalSourceId: matchedItem?.supplementalSourceId,
        sourceType: matchedItem?.sourceType ?? 'pipeline',
        sourceKey,
        displayConfig: matchedItem?.displayConfig ?? null,
        data: itemData
      })
    }

    for (const q of dataQueries) {
      if (!q.data?.result) continue
      const { sourceType, sourceKey, responseItemsPath, items, result } = q.data
      const rows = getResponseRows(result, sourceType, responseItemsPath)
      if (!Array.isArray(rows) || !items.length) continue

      for (const src of items) {
        const line = itemByStatusReportItemId.get(src.statusReportItemId)
        if (!line) continue
        const matched = filterPipelineRowsForLine(rows, src.matchConfig, line)
        if (!matched.length) continue
        const itemData = wrapMatchedRowsForDisplay(matched, src.displayConfig)
        pushSource(src.statusReportItemId, itemData, src, sourceKey)
      }
    }

    return merged
  // dataQueries is read inside but intentionally excluded from deps; dataSignal
  // (joined dataUpdatedAt timestamps) changes whenever any query's data changes,
  // which is the only case where the derived supplementalData should recompute.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSignal, itemByStatusReportItemId])

  const isLoading = configLoading || dataQueries.some(q => q.isLoading)

  const value = useMemo(() => ({
    getItemSupplementalData: (statusReportItemId) => supplementalData[statusReportItemId] ?? null,
    isLoading
  }), [supplementalData, isLoading])

  return (
    <StatusReportItemSupplementalContentContext.Provider value={value}>
      {children}
    </StatusReportItemSupplementalContentContext.Provider>
  )
}

StatusReportItemSupplementalContentProvider.propTypes = {
  children: PropTypes.node
}

export default StatusReportItemSupplementalContentProvider
